<template>
  <div class="w-full h-full">
    <Alert variant="info" class="mb-4">
      <Info />
      <AlertTitle>What are custom offers?</AlertTitle>
      <AlertDescription>
        Custom offers let you create your own self-funded cashback or points-based promotions for any
        retailer in the network. You set the reward, eligibility and spend conditions, and fund the
        cashback yourself. Boogi handles matching transactions and responding with webhooks. It is
        your own responsibility to manage settlements.
      </AlertDescription>
    </Alert>

    <!-- Acceptance modal: must be accepted before creating a custom offer -->
    <AlertDialog v-model:open="showAddOfferWarning">
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle class="flex items-center gap-2">
            <TriangleAlert class="w-5 h-5 text-amber-500" />
            Before you continue
          </AlertDialogTitle>
          <AlertDialogDescription>
            Before publishing any self-funded cashback or points-based promotion, Boogi strongly
            advises that you obtain prior written consent from the relevant retailer. Should you
            choose to publish a promotion without such approval, you do so entirely at your own risk.
            Boogi accepts no responsibility or liability for any promotions published without retailer
            authorisation.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction @click="$router.push('/add-offer')">I understand, continue</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    <Card>
      <CardHeader>
        <CardTitle>Custom offers</CardTitle>
        <CardDescription>Manage your custom offers</CardDescription>
        <CardAction>
          <Button size="sm" @click="showAddOfferWarning = true">
            <Plus class="w-4 h-4 mr-1" />Add offer
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent>
        <DataTable :headers="headersTable1" :items="retailers" :loading="loading"
          v-model:server-options="serverOptions" :server-items-length="serverItemsLength"
          :error="error"
          @retry="getRetailers(serverOptions.page - 1, serverOptions.rowsPerPage)"
          searchable
          search-placeholder="Search by retailer name"
          :search="retailerSearchInput"
          @update:search="retailerSearchInput = $event"
          @search="getRetailerBySearch(0, serverOptions.rowsPerPage, retailerSearchInput)"
          @clear-search="onClearSearch()"
          :filter-groups="offerFilterGroups"
          :filters="offerFilters"
          @filter-change="onOfferFilterChange">
          <template #item-retailer="retailers">
            <div class="flex justify-start items-center gap-2.5">
              <img class="w-8 h-8 rounded-md border border-border object-contain shrink-0 bg-white" alt=""
                :src="retailers.displayInformation.logo" />
              <div class="min-w-0">
                <p class="text-sm font-medium truncate" :title="retailers.retailer.retailerName">{{ retailers.retailer.retailerName }}</p>
                <CopyId class="mt-1" :value="retailers.id" :max-width="120" />
              </div>
            </div>
          </template>

          <!-- Offer type and country moved out of the offer cell into their own column. -->
          <template #item-detail="retailers">
            <div class="flex flex-wrap gap-1 items-center">
              <Badge variant="outline">{{ formatOfferType(retailers.offerType) }}</Badge>
              <Badge variant="outline" v-if="retailers.country">{{ retailers.country }}</Badge>
            </div>
          </template>

          <template #item-cashbackAmount="retailers">
            <span class="text-sm tabular-nums whitespace-nowrap">{{ retailers.cashbackRules[0].conditions[0].value.endUserValue }}{{ ['PERCENTAGE', 'PERCENTAGE_MAX'].includes(retailers.cashbackRules[0].conditions[0].value.type) ? '%' : ' ' + (retailers.currencyList[0] || '') }}<span v-if="retailers.cashbackRules[0].conditions[0].value.maximum"> (up to £{{ retailers.cashbackRules[0].conditions[0].value.maximum }})</span></span>
            <p v-if="retailers.spendingCapped || retailers.spendingCapAmount > 0" class="text-xs text-muted-foreground mt-1">Cashback capped at £{{ retailers.spendingCapAmount }} per user</p>
            <p v-else class="text-xs text-muted-foreground mt-1">Uncapped</p>
          </template>

          <template #item-action="retailers">
            <div class="flex justify-end items-center gap-1">
              <Button variant="outline" size="sm" @click="$router.push(`/retailer/${retailers.retailer.id}/offer/${retailers.id}`)">View</Button>
            </div>
          </template>
        </DataTable>
      </CardContent>
    </Card>
  </div>
</template>

<script setup></script>

<script>
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardAction,
  Button,
  Badge,
  Alert,
  AlertTitle,
  AlertDescription,
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
  CopyId,
} from '@boogi/ui'
import { Plus, TriangleAlert, Info } from 'lucide-vue-next'
import { DataTable } from '@boogi/ui'

export default {
  name: 'your-offers-catalogue',
  components: {
    DataTable,
    Card,
    CardHeader,
    CardTitle,
    CardDescription,
    CardContent,
    CardAction,
    Button,
    Badge,
    Alert,
    AlertTitle,
    AlertDescription,
    AlertDialog,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogCancel,
    AlertDialogAction,
    CopyId,
    Plus,
    TriangleAlert,
    Info,
  },
  props: {},

  data() {
    return {
      retailerSearchInput: '',
      showAddOfferWarning: false,
      serverOptions: {
        page: 1,
        rowsPerPage: 25,
      },
      serverItemsLength: undefined,
      showErrorModal: false,
      showSuccessModal: false,
      showFormModal: false,
      pecentageNotRight: '',

      activateOfferRequest: {
        splitOfCashbackPercentage: {
          endUser: 50,
          client: 50,
        },
        activate: true,
      },

      selectedRetailer: undefined,
      loading: true,
      error: false,
      headersTable1: [
        { text: 'Offer', value: 'retailer', width: 400 },
        { text: 'Detail', value: 'detail' },
        { text: 'Cashback amount', value: 'cashbackAmount' },
        { text: 'Action', value: 'action' },
      ],
      retailers: [],
      offerTypes: [
        { id: 'PAYMENT', text: 'Payment-linked offers' },
        { id: 'ONLINE', text: 'Affiliate links' },
        { id: 'CLICK_TO_ACTIVATE', text: 'Click to activate' },
      ],

      offerType: [],
    }
  },
  created() {
    this.getRetailers(0, 25)
  },
  methods: {
    formatOfferType(type) {
      const map = {
        PAYMENT: 'Payment-linked',
        ONLINE: 'Affiliate',
        CLICK_TO_ACTIVATE: 'Click to activate',
      }
      return map[type] || type
    },

    onClearSearch() {
      this.retailerSearchInput = ''
      this.serverOptions = { page: 1, rowsPerPage: this.serverOptions.rowsPerPage || 25 }
      this.getRetailers(0, this.serverOptions.rowsPerPage)
    },

    onOfferFilterChange(updated) {
      const typeIds = (updated && updated.offerType) || []
      this.offerType = this.offerTypes.filter((o) => typeIds.includes(o.id))
      this.serverOptions = { page: 1, rowsPerPage: this.serverOptions.rowsPerPage || 25 }
      if (this.retailerSearchInput) {
        this.getRetailerBySearch(0, this.serverOptions.rowsPerPage, this.retailerSearchInput)
      } else {
        this.getRetailers(0, this.serverOptions.rowsPerPage)
      }
    },

    async getRetailerBySearch(page, size, retailerSearch) {
      const token = localStorage.getItem('token')
      const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

      if (this.offerType.length > 0) {
        var offerType = []
        this.offerType.forEach((element) => {
          offerType.push(element.id)
        })
      }

      this.loading = true
      this.error = false
      try {
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/catalogue/offer/byoo?page=${page}&size=${size}&search=${retailerSearch}${this.offerType.length > 0 ? '&type=' + offerType : ''}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()
          this.retailers = object.content
          this.serverItemsLength = object.totalElements
        } else {
          this.error = true
        }
      } catch (e) {
        this.error = true
      } finally {
        this.loading = false
      }
    },

    async getRetailers(page, size) {
      const token = localStorage.getItem('token')
      const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

      if (this.offerType.length > 0) {
        var offerType = []
        this.offerType.forEach((element) => {
          offerType.push(element.id)
        })
      }

      this.loading = true
      this.error = false
      try {
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/catalogue/offer/byoo?page=${page}&size=${size}${this.offerType.length > 0 ? '&type=' + offerType : ''}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()

          this.serverItemsLength = object.totalElements
          this.retailers = object.content
        } else {
          this.error = true
        }
      } catch (e) {
        this.error = true
      } finally {
        this.loading = false
      }
    },
  },
  computed: {
    offerFilterGroups() {
      return [
        {
          key: 'offerType',
          label: 'Offer types',
          options: this.offerTypes.map((o) => ({ value: o.id, label: o.text })),
        },
      ]
    },
    offerFilters() {
      return {
        offerType: this.offerType.map((o) => o.id),
      }
    },
  },
  mounted() {
    document.title = 'Dashboard | Custom offers'
  },
  watch: {
    serverOptions: {
      deep: true,
      handler(val) {
        this.getRetailers(val.page - 1, val.rowsPerPage)
      },
    },
    activateOfferRequest: {
      deep: true,
      handler: function (newValue) {
        if (newValue.splitOfCashbackPercentage.endUser == '') {
          this.activateOfferRequest.splitOfCashbackPercentage.endUser = 0
        }

        if (newValue.splitOfCashbackPercentage.endUser > 100) {
          this.activateOfferRequest.splitOfCashbackPercentage.endUser = 100
          this.pecentageNotRight = 'The percentage needs to be less than or equal to 100'
        } else if (newValue.splitOfCashbackPercentage.endUser < 0) {
          this.activateOfferRequest.splitOfCashbackPercentage.endUser = 0
          this.pecentageNotRight = 'The percentage needs to be greater than or equal to 0'
        }
        this.pecentageNotRight = ''
        this.activateOfferRequest.splitOfCashbackPercentage.client =
          100 - newValue.splitOfCashbackPercentage.endUser
      },
    },
  },
}
</script>
